<template>
  <section class="home-container">
    <div class="statsheet-header-block">
      <h1 class="statsheet-header-title">
        Your Profile
        <span class="statsheet-header-sub">
          Your professional foundation - growing with every new experience.
        </span>
      </h1>
      <div class="statsheet-header-desc">
        <strong>This version reflects your confirmed background and aspirations.</strong>
        <br>
        Your existing experiences are locked, but you can continue building your story by adding new roles, skills, and goals.
      </div>
      <b-button
        v-if="user && user?._id && user?.isStatsheetLocked"
        class="review-launch-btn"
        @click="openVerifiedModal"
      >
        <i class="mdi mdi-star"></i>
        Get Verified
      </b-button>
    </div>
    <div class="content-container container">
      <!-- spec-admin-gated-company-access: the person's own company access request -->
      <div v-if="myAccessRequest" class="access-pending-banner" role="status">
        <i class="mdi mdi-clock-outline"></i>
        <span v-if="myAccessRequest.pendingVerification">
          Verify your email to send your request to join {{ myAccessCompanyName }} to its admins.
        </span>
        <span v-else>
          Your company admin has been asked to approve your access to {{ myAccessCompanyName }}. You'll be notified once they decide.
        </span>
        <b-button type="is-text" size="is-small" :loading="refreshingAccess" @click="refreshAccess">Check again</b-button>
        <b-button type="is-text" size="is-small" :loading="withdrawingAccess" @click="withdrawMyAccess">Withdraw request</b-button>
      </div>
      <div v-if="isLoading" class="loading-container">
        <b-loading :is-full-page="false" :active="isLoading"></b-loading>
        <p class="loading-text">Loading your profile...</p>
      </div>

      <template v-else>
        <div class="statsheet-update-card">
          <b-button
            type="is-primary"
            class="statsheet-update-btn"
            @click="showStatsheetUpdateModal = true"
          >
            Use Teamwrkr AI to update your profile
          </b-button>
          <p class="statsheet-update-help">
            Free form type here any updates to your career, skills, etc. We can handle the rest.
          </p>
        </div>

        <div class="statsheet-sections">
          <div class="statsheet-section-full">
            <AspirationsSection :aspirations="user.aspirations" :self="true" />
          </div>

          <div class="statsheet-section-full">
            <recent-skills-section
              :skills="user.recentSkills"
              :editable="true"
              :config="config"
            />
          </div>
        </div>
      </template>
    </div>

    <get-verified-modal
      v-if="user && user._id"
      :user="user"
      v-model:showModal="showVerifiedModal"
    />

    <b-modal
      v-model="showStatsheetUpdateModal"
      :can-cancel="['escape', 'outside', 'button']"
      :width="520"
      trap-focus
    >
      <div class="modal-content">
        <h2 class="modal-title">
          <i class="mdi mdi-file-document-edit"></i>
          Update My Profile
        </h2>
        <p class="modal-desc">
          Share any new role, project, accomplishment, skill, certification, or goal update and Teamwrkr will handle the rest.
        </p>
        <b-field label="Profile update">
          <b-input
            v-model="statsheetUpdateText"
            type="textarea"
            placeholder="Type any career, skills, or goals updates here..."
            rows="5"
          />
        </b-field>
        <div class="modal-actions modal-actions--end">
          <b-button
            type="is-primary"
            :loading="savingStatsheetUpdate"
            :disabled="!statsheetUpdateText.trim()"
            @click="saveStatsheetUpdate"
          >
            Save
          </b-button>
          <b-button type="is-text" @click="closeStatsheetUpdateModal">
            Cancel
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Profile Locked Modal -->
    <b-modal
      v-model="showStatsheetModal"
      :can-cancel="false"
      :width="600"
      trap-focus
    >
      <div v-if="!showStatsheetModalReject" class="modal-content">
        <h2 class="modal-title">
          <i class="mdi mdi-lock-open-outline"></i>
          Welcome to Teamwrkr!
        </h2>
        <p class="modal-desc">
          Please take a look at your new AI generated
          <router-link
            :to="{ path: '/profile/' + user._id }"
            custom
            v-slot="{ href, navigate }"
          >
            <a :href="href" target="_blank" rel="noopener" @click="navigate">
              Profile
            </a>
          </router-link> and approve to unlock your full use of the platform.
        </p>
        <div class="modal-actions">
          <b-button type="is-primary" @click="unlockStatsheet">
            Looks great, lock my Profile!
          </b-button>
          <!-- <b-button type="is-secondary" @click="showStatsheetModalReject = true"> -->
          <b-button
            type="is-secondary"
            tag="a"
            href="/reject-profile"
            target="_blank"
            rel="noopener"
          >
            Reject my Profile
          </b-button>
        </div>
      </div>
      <div v-else class="modal-content">
        <h2 class="modal-title">
          <i class="mdi mdi-emoticon-sad-outline"></i>
          Oh No!
        </h2>
        <div v-if="!feedbackSubmitted">
          <p class="modal-desc">
            Please leave us some feedback on what you don't like about your Profile.
            <b-input
              v-model="feedback"
              type="textarea"
              placeholder="Your feedback..."
              rows="4"
            ></b-input>
          </p>
          <div class="modal-actions">
            <b-button type="is-primary" @click="submitFeedback">
              Submit Feedback
            </b-button>
            <b-button type="is-secondary" @click="showStatsheetModalReject = false">
              Back
            </b-button>
          </div>
        </div>
        <div v-else>
          <p class="modal-desc">
            Thank you for your feedback! We'll use it to improve your Profile and the ˇTeamwrkr platform. Someone will be in touch soon.
          </p>
          <!-- <div class="modal-actions">
            <b-button type="is-primary" @click="closeStatsheetModal">
              Close
            </b-button>
          </div> -->
        </div>
      </div>
    </b-modal>

  </section>
</template>

<script>
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'
import AspirationsSection from '@/components/home/AspirationsSection.vue'
import GetVerifiedModal from '@/components/home/GetVerifiedModal.vue'
import RecentSkillsSection from '@/components/home/RecentSkillsSection.vue'

export default {
  name: 'HomePage',

  components: {
    AspirationsSection,
    GetVerifiedModal,
    RecentSkillsSection,
  },

  data() {
    return {
      refreshingAccess: false,
      withdrawingAccess: false,
      myAccessRequest: null,
      showVerifiedModal: false,
      feedback: '',
      feedbackSubmitted: false,
      showStatsheetModal: false,
      showStatsheetModalReject: false,
      showStatsheetUpdateModal: false,
      statsheetUpdateText: '',
      savingStatsheetUpdate: false,
      config: {
        headers: { 'Authorization': this.$cookies.get('teamwrkr-token') }
      }
    }
  },

  beforeCreate() {
    if (!this.$cookies.isKey('teamwrkr-token')) {
      this.$router.push('/welcome')
    }
  },

  computed: {
    // Pull reactive state directly from Pinia
    ...mapState(useUserStore, ['user', 'notifications', 'loading']),
    isLoading() {
      return this.loading || this.user === null
    },
    myAccessCompanyName() {
      return this.myAccessRequest?.company?.name || 'your company'
    },
  },

  // spec-admin-gated-company-access: re-check the pending-access banner when the tab regains focus.
  mounted() {
    window.addEventListener('focus', this.refreshAccess)
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.refreshAccess)
  },

  async created() {
    const userStore = useUserStore()
    const token = this.$cookies.get('teamwrkr-token')

    if (token) {
      await userStore.fetchUser(token)
    }
    await this.loadMyAccessRequest()

    if (userStore.user?._id && userStore.user.isStatsheetLocked === false) {
      this.$router.push(`/profile/${userStore.user._id}`)
    }
  },

  // watch: {
  //   user: {
  //     handler(newUser) {
  //       if (newUser) {
  //         console.log('NEW User is now available in Home.vue:', newUser)
  //         // this.notifications = newUser.notifications || []
  //       }
  //     },
  //     immediate: true, // fires even on initial load if user is already set
  //     deep: true
  //   }
  // },

  methods: {
    // spec-admin-gated-company-access: a company-less person's own live request (open or still
    // waiting for email verification), from GET /users/access-requests/mine.
    async loadMyAccessRequest() {
      if (!this.user?._id || this.user.company) {
        this.myAccessRequest = null
        return
      }
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_API_URL}/users/access-requests/mine`, this.config)
        this.myAccessRequest = response?.data?.request || null
      } catch (err) {
        this.myAccessRequest = null
      }
    },
    async refreshAccess() {
      if (this.refreshingAccess || !this.user?._id || this.user.company) return
      const token = this.$cookies.get('teamwrkr-token')
      if (!token) return
      this.refreshingAccess = true
      try {
        await useUserStore().fetchUser(token, { force: true })
        await this.loadMyAccessRequest()
      } finally {
        this.refreshingAccess = false
      }
    },
    async withdrawMyAccess() {
      if (!this.myAccessRequest?.id || this.withdrawingAccess) return
      this.withdrawingAccess = true
      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/access-requests/${encodeURIComponent(this.myAccessRequest.id)}/withdraw`,
          {},
          this.config,
        )
        this.myAccessRequest = null
        this.$buefy.toast.open({ message: 'Your access request was withdrawn.', type: 'is-info' })
      } catch (err) {
        this.$buefy.toast.open({ message: err?.response?.data?.message || 'Unable to withdraw the request.', type: 'is-danger' })
        await this.loadMyAccessRequest()
      } finally {
        this.withdrawingAccess = false
      }
    },
    async submitFeedback() {
      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/feedback`,
          {
            area: 'Profile Rejection',
            message: this.feedback,
            user: this.user?._id,
          },
        )
        this.feedbackSubmitted = true
        this.feedback = ''
      } catch (error) {
        console.log('Error submitting feedback:', error)
      }
    },

    async unlockStatsheet() {
      try {
        await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/lock-statsheet`,
          {},
          this.config,
        )
        this.closeStatsheetModal()
      } catch (error) {
        console.error('Error unlocking statsheet:', error)
      }
    },

    closeStatsheetModal() {
      this.showStatsheetModal = false
    },
    openVerifiedModal() {
      this.showVerifiedModal = true
    },
    closeStatsheetUpdateModal() {
      this.showStatsheetUpdateModal = false
      this.statsheetUpdateText = ''
    },
    async saveStatsheetUpdate() {
      const text = this.statsheetUpdateText.trim()
      if (!text) return

      this.savingStatsheetUpdate = true
      try {
        const updatedUser = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/update-statsheet`,
          { text },
          this.config
        )
        if (updatedUser?.data) {
          const userStore = useUserStore()
          userStore.setUser(updatedUser.data)
        }
        this.$buefy.toast.open({
          message: 'Profile updated successfully.',
          type: 'is-success'
        })
        this.closeStatsheetUpdateModal()
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to update profile.',
          type: 'is-danger'
        })
      } finally {
        this.savingStatsheetUpdate = false
      }
    },
  },
}
</script>


<style lang="scss" scoped>
.access-pending-banner {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-bottom: $space-4;
  padding: $space-3 $space-4;
  border-radius: $radius-md;
  background: $color-primary-soft;
  color: $color-primary-dark;
  border: 1px solid rgba($color-primary, 0.3);
}

.home-container {
  font-family: 'Nunito', sans-serif;
  background-color: $color-surface-cool;
  min-height: 100vh;
}
.statsheet-header-block {
  max-width: 900px;
  margin: 0 auto 2.5rem auto;
  padding: 2.2rem 1.2rem 1.2rem 1.2rem;
  text-align: center;
}

.statsheet-header-title {
  font-size: 2.2rem;
  font-weight: 800;
  color: $color-primary;
  margin-bottom: 0.5rem;
  line-height: 1.1;
  .statsheet-header-sub {
    display: block;
    font-size: 1.15rem;
    font-weight: 400;
    color: $color-text-muted;
    margin-top: 0.5rem;
  }
}

.statsheet-header-desc {
  font-size: 1.08rem;
  color: $color-text;
  margin-top: 0.7rem;
  margin-bottom: 0.5rem;
  line-height: 1.5;
}

.review-launch-btn {
  margin-top: 1.25rem;
  border-radius: $radius-pill;
  box-shadow: none;
  background: $color-surface;
  color: $color-primary;
  border: 1px solid $color-primary;
}

@media (max-width: 900px) {
  .statsheet-header-block {
    padding: 1.2rem 0.5rem 0.7rem 0.5rem;
    margin-bottom: 1.2rem;
  }
  .statsheet-header-title {
    font-size: 1.3rem;
    .statsheet-header-sub {
      font-size: 0.98rem;
    }
  }
  .statsheet-header-desc {
    font-size: 0.98rem;
  }
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;

  .loading-text {
    margin-top: 1.5rem;
    font-size: 1.1rem;
    color: $color-text-subtle;
  }
}
.content-container {
  padding-bottom: 3rem;
}

.statsheet-update-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: $color-surface;
  border: 1px solid $color-border;
  border-radius: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.statsheet-update-btn {
  min-height: 3rem;
  padding: 0 1.5rem;
  font-weight: 700;
}

.statsheet-update-help {
  margin: 0;
  color: $color-text-muted;
  text-align: center;
}

.statsheet-sections {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.statsheet-section-full {
  width: 100%;
}

.statsheet-section-full :deep(.section) {
  width: 100%;
  max-width: 100%;
  flex: none;
}

.modal-content {
  padding: 1.5rem 1rem;
  background: $color-surface;
  border-radius: 0.75rem;
  color: $color-text;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}
.modal-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: $color-primary;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
  i {
    color: $color-warning;
    font-size: 1.7rem;
  }
}
.modal-desc {
  font-size: 1.05rem;
  color: $color-text-muted;
  margin-bottom: 0.5rem;
}
.modal-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.modal-actions--end {
  justify-content: flex-end;
}

@media (max-width: 900px) {
  .statsheet-update-card {
    padding: 1rem;
  }
}
</style>
